import { useCallback, useEffect, useState } from 'react';

/**
 * `useState`-shaped hook backed by `localStorage`. The state is initialised
 * from `localStorage[key]` (parsed via JSON) on first render and re-persisted
 * on every change. Cross-tab sync via the `storage` event keeps multiple
 * windows aligned.
 *
 * - `serialize`/`deserialize` default to `JSON.stringify`/`JSON.parse` and
 *   can be overridden for primitives that you'd rather store as a raw string
 *   (e.g. `'true'`/`'false'` instead of `'true'` JSON).
 * - localStorage failures (private browsing / quota) are swallowed silently
 *   so the hook always returns a usable [value, setter] pair.
 */
export function usePersistedState<T>(
  key: string,
  initial: T,
  serialize: (v: T) => string = JSON.stringify,
  deserialize: (s: string) => T = JSON.parse,
): [T, (next: T) => void] {
  const [value, setValueState] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : deserialize(raw);
    } catch {
      return initial;
    }
  });

  const setValue = useCallback(
    (next: T) => {
      setValueState(next);
      try {
        localStorage.setItem(key, serialize(next));
      } catch {
        // localStorage may be unavailable — fail open.
      }
      // Same-tab listeners — `storage` only fires in *other* tabs.
      window.dispatchEvent(
        new CustomEvent('itow-persisted-state-changed', { detail: { key } }),
      );
    },
    [key, serialize],
  );

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key || e.newValue === null) return;
      try {
        setValueState(deserialize(e.newValue));
      } catch {
        // ignore unparseable cross-tab writes
      }
    };
    const onCustom = (e: Event) => {
      const ce = e as CustomEvent<{ key: string }>;
      if (ce.detail?.key !== key) return;
      try {
        const raw = localStorage.getItem(key);
        if (raw !== null) setValueState(deserialize(raw));
      } catch {
        // ignore
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('itow-persisted-state-changed', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('itow-persisted-state-changed', onCustom);
    };
  }, [key, deserialize]);

  return [value, setValue];
}
